import { useEffect, useRef, useState } from "react";
import PSPDFKit from 'pspdfkit';
import { useGetDocumentById } from "../../../hooks/ReactQueryHooks/useGetDocument";
import { PSPDFKIT_LICENSE_KEY } from "../../../environment";

const SIGNATURE_FIELD_NAME = "practitioner-signature_af_image";
const SIGNATURE_FIELD_RETRY_ATTEMPTS = 6;
const SIGNATURE_FIELD_RETRY_DELAY_MS = 150;
const SUPPORTED_SIGNATURE_IMAGE_TYPES = new Set([
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
]);

const toFormFieldString = (value) => {
    if (value == null) return null;
    return String(value);
};

const resolveSignatureContentType = (blob, signUrl) => {
    const blobType = blob.type?.toLowerCase();
    if (blobType && SUPPORTED_SIGNATURE_IMAGE_TYPES.has(blobType)) {
        return blobType === "image/jpg" ? "image/jpeg" : blobType;
    }
    const url = signUrl?.toLowerCase() ?? "";
    if (url.includes(".png") || url.startsWith("data:image/png")) {
        return "image/png";
    }
    return "image/jpeg";
};

const isSignatureWidget = (annotation) =>
    annotation instanceof PSPDFKit.Annotations.WidgetAnnotation &&
    (annotation.formFieldName === SIGNATURE_FIELD_NAME ||
        annotation.formFieldName?.includes("practitioner-signature"));

const findSignatureWidgetFields = async (instance) => {
    const totalPages = instance.totalPageCount;
    const signatureFields = [];

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const annotations = await instance.getAnnotations(pageIndex);
        annotations
            .filter(isSignatureWidget)
            .forEach((signatureField) => {
                signatureFields.push({ pageIndex, signatureField });
            });
    }

    return signatureFields;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const sanitizeToPlainText = (value) =>
    String(value ?? "")
        .normalize("NFC")
        // smart single quotes / primes -> '
        .replace(/[\u2018\u2019\u201A\u201B\u2032\u00B4\u0060]/g, "'")
        // smart double quotes / primes -> "
        .replace(/[\u201C\u201D\u201E\u201F\u2033\u00AB\u00BB]/g, '"')
        // dashes / minus -> -
        .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212]/g, "-")
        // ellipsis -> ...
        .replace(/\u2026/g, "...")
        // bullets -> -
        .replace(/[\u2022\u2023\u25E6\u2043]/g, "-")
        // zero-width / BOM -> removed
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        // any whitespace (incl. non-breaking, tabs, newlines) -> single space
        .replace(/[\s\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]+/g, " ")
        // control characters -> removed
        .replace(/[\u0000-\u001F\u007F]/g, "")
        // strip anything outside printable ASCII + Latin-1 (font-encodable) range
        .replace(/[^\u0020-\u007E\u00A0-\u00FF]/g, "");

const handlePastePlainText = (event) => {
    const target = event.target;
    if (!target || (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA")) {
        return;
    }

    const clipboard = event.clipboardData || window.clipboardData;
    if (!clipboard) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    const plainText = sanitizeToPlainText(clipboard.getData("text/plain"));
    const ownerDocument = target.ownerDocument || document;

    // Genuine browser text insertion at the caret. This fires native input
    // events that PSPDFKit's (React-based) form field tracking reacts to, so
    // the value persists without requiring a follow-up manual edit.
    if (typeof ownerDocument.execCommand === "function" &&
        ownerDocument.execCommand("insertText", false, plainText)) {
        return;
    }
};

const findSignatureWidgetFieldsWithRetry = async (instance) => {
    for (let attempt = 0; attempt < SIGNATURE_FIELD_RETRY_ATTEMPTS; attempt++) {
        const signatureFields = await findSignatureWidgetFields(instance);
        if (signatureFields.length > 0) {
            return signatureFields;
        }
        if (attempt < SIGNATURE_FIELD_RETRY_ATTEMPTS - 1) {
            await wait(SIGNATURE_FIELD_RETRY_DELAY_MS);
        }
    }
    return [];
};

const signUrlToBlob = async (signUrl) => {
    if (signUrl.startsWith("data:")) {
        const response = await fetch(signUrl);
        if (!response.ok) {
            throw new Error("Could not read signature image data.");
        }
        return response.blob();
    }

    const response = await fetch(signUrl);
    if (!response.ok) {
        throw new Error("Could not load signature image.");
    }
    return response.blob();
};

const stampSignature = async (instance, signUrl) => {
    const blob = await signUrlToBlob(signUrl);
    const contentType = resolveSignatureContentType(blob, signUrl);
    const attachmentBlob =
        blob.type === contentType ? blob : new Blob([blob], { type: contentType });
    const signatureFields = await findSignatureWidgetFieldsWithRetry(instance);

    if (signatureFields.length === 0) {
        throw new Error("Signature field not found in the PDF. Please wait a moment and try again.");
    }

    const imageAttachmentId = await instance.createAttachment(attachmentBlob);

    for (const { pageIndex, signatureField } of signatureFields) {
        const imageAnnotation = new PSPDFKit.Annotations.ImageAnnotation({
            pageIndex,
            boundingBox: signatureField.boundingBox,
            contentType,
            imageAttachmentId,
        });
        await instance.create(imageAnnotation);
    }
};

export const populatePdfForm = async (instance, initialPdfFormValues) => {
    if (!instance || !initialPdfFormValues) return;

    const formFields = await instance.getFormFields();
    const signatureValue = initialPdfFormValues[SIGNATURE_FIELD_NAME];
    const textFieldValues = {};

    for (const formField of formFields) {
        const value = initialPdfFormValues[formField.name];
        if (value == null || value === "" || formField.name === SIGNATURE_FIELD_NAME) continue;
        textFieldValues[formField.name] = toFormFieldString(value);
    }

    if (Object.keys(textFieldValues).length > 0) {
        instance.setFormFieldValues(textFieldValues);
    }

    if (signatureValue) {
        await stampSignature(instance, signatureValue);
    }
};

export const validatePdfBeforeNext = async (instance, initialPdfFormValues) => {
    if (!initialPdfFormValues) {
        return { ok: false, message: "Prescription data is still loading. Please wait and try again." };
    }
    if (!instance) {
        return { ok: false, message: "PDF is still loading. Please wait and try again." };
    }

    const formFields = await instance.getFormFields();
    const signatureRequired = formFields.some((f) => f.name === SIGNATURE_FIELD_NAME);
    const signatureOnProfile = Boolean(initialPdfFormValues[SIGNATURE_FIELD_NAME]);

    if (signatureRequired && !signatureOnProfile) {
        return {
            ok: false,
            message: "No signature on your profile. Please update your profile before continuing.",
        };
    }

    if (signatureRequired && signatureOnProfile) {
        try {
            await populatePdfForm(instance, initialPdfFormValues);
        } catch (err) {
            return {
                ok: false,
                message: err?.message || "Could not apply signature to the prescription. Please try again.",
            };
        }
    }

    return { ok: true };
};

export default function PdfViewerComponent({ document, documentId, instanceRef, initialPdfFormValues }) {
    const containerRef = useRef(null);
    const [base64Url, setBase64Url] = useState(null);
    const [pdfReady, setPdfReady] = useState(false);

    const onSuccess = (data) => {
        setBase64Url(data?.content?.[0]?.attachment?.base64String);
    };
    useGetDocumentById({ onSuccess, id: documentId });

    useEffect(() => {
        const baseUrl = `${window.location.protocol}//${window.location.host}/`;
        const container = containerRef.current;
        let cancelled = false;
        const pdfSource = document || base64Url;

        setPdfReady(false);
        instanceRef.current = null;

        if (!pdfSource) {
            return undefined;
        }

        (async () => {
            try {
                const instance = await PSPDFKit.load({
                    disableWebAssemblyStreaming: true,
                    container,
                    document: pdfSource,
                    toolbarItems: [],
                    baseUrl,
                    licenseKey: PSPDFKIT_LICENSE_KEY,
                });

                if (cancelled) {
                    PSPDFKit.unload(container);
                    return;
                }

                instance.setViewState((viewState) => viewState.set("showToolbar", false));
                instanceRef.current = instance;

                if (instance.contentDocument) {
                    instance.contentDocument.addEventListener("paste", handlePastePlainText, true);
                }

                setPdfReady(true);
            } catch (err) {
                console.error("PDF viewer failed to load:", err);
            }
        })();

        return () => {
            cancelled = true;
            setPdfReady(false);
            instanceRef.current = null;
            if (container && PSPDFKit) {
                PSPDFKit.unload(container);
            }
        };
    }, [document, base64Url, instanceRef]);

    useEffect(() => {
        if (!pdfReady || !initialPdfFormValues || !instanceRef.current) {
            return undefined;
        }

        let cancelled = false;

        (async () => {
            try {
                await populatePdfForm(instanceRef.current, initialPdfFormValues);
            } catch (err) {
                if (!cancelled) {
                    console.error("PDF form population failed:", err);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [pdfReady, initialPdfFormValues, instanceRef]);

    return <div ref={containerRef} style={{ width: "100%", height: "80vh" }} />;
};
